import {
  LOCAL_VARIABLE_SCOPE,
  GLOBAL_VARIABLE_SCOPE,
  OAUTH_VARIABLE_SCOPE,
  SENSITIVE_VARIABLE_SCOPE
} from "constants/variables";
import { trackException } from "services/errorTracker";
import { mongoObjectId } from "services/objectid";
import keyConverter from "services/key-converter";
import Immutable from "immutable";

export const ReferenceCounts = Immutable.Record({
  saved: 0,
  unsaved: 0
});

export class VariableRecord extends Immutable.Record({
  id: null,
  name: "",
  key: "",
  defaultValue: "",
  scope: "",
  responseId: null, // should be null for globals
  originId: null, // for locals originId == responseId, for globals this should be set
  displayName: "",
  responseReferences: Immutable.List(),
  active: true,
  type: "string",
  // client-side only attributes
  responseReferenceCounts: Immutable.Map(),
  modified: false,
  isValid: true
}) {}

/**
 * @param {VariableRecord} variable
 * @param {string} responseId
 * @param {Number} additionalReferences
 * @returns {object}
 */
export function addResponseId(variable, responseId, additionalReferences = 1) {
  if (!variable.get("responseReferenceCounts").has(responseId)) {
    return variable.setIn(
      ["responseReferenceCounts", responseId],
      new ReferenceCounts({
        unsaved: additionalReferences
      })
    );
  }

  return variable.updateIn(
    ["responseReferenceCounts", responseId, "unsaved"],
    count => count + additionalReferences
  );
}

/**
 * @param {VariableRecord} variable
 * @param {string} responseId
 * @returns {object}
 */
export function deleteResponseId(variable, responseId) {
  if (!variable.get("responseReferenceCounts").has(responseId)) {
    return variable;
  }

  return variable.updateIn(
    ["responseReferenceCounts", responseId, "unsaved"],
    count => count - 1
  );
}

/**
 * @param {VariableRecord} variable
 * @param {string} responseId
 * @returns {boolean}
 */
export function toDelete(variable, responseId) {
  // if saved is 0 everywhere other than the current responseId
  const anySaved = variable
    .responseReferenceCounts
    .filter((v, responseIdKey) => responseIdKey !== responseId)
    .some(counts => counts.saved !== 0);

  const counts = variable.getIn(["responseReferenceCounts", responseId]);

  return counts && !anySaved && (counts.saved + counts.unsaved) === 0;
}

/**
 * Checks if a variable will be deleted if we remove the given responseId
 * @param {Immutable.Record} variable
 * @param {String} responseId
 * @returns {Boolean}
 */
export function willBeDeleted(variable, responseId) {
  const removed = deleteResponseId(variable, responseId);

  return toDelete(removed, responseId);
}

/**
 * @param {VariableRecord} variable
 * @param {string} responseId
 * @returns {boolean}
 */
export function isDirty(variable, responseId) {
  if (!variable.responseReferenceCounts.has(responseId)) {
    return false;
  }

  return variable.getIn(["responseReferenceCounts", responseId, "unsaved"]) !== 0 || variable.get("modified");
}

/**
 * @param {unknown} variable
 * @returns {boolean}
 */
export function isActive(variable) {
  return variable.responseReferenceCounts
    .map(counts => counts.saved + counts.unsaved)
    .some(value => value !== 0);
}

/**
 * @param {unknown} variable
 * @returns {boolean}
 */
export function isSaved(variable) {
  if (![
    LOCAL_VARIABLE_SCOPE,
    GLOBAL_VARIABLE_SCOPE,
    OAUTH_VARIABLE_SCOPE,
    SENSITIVE_VARIABLE_SCOPE
  ].includes(variable.scope)) {
    return true;
  }


  return variable.responseReferenceCounts
    .map(counts => counts.saved)
    .some(v => v !== 0);
}

/**
 * Checks if this variable is valid
 * @param {Immutable.Record} variable
 * @returns {Boolean}
 */
export const isValid = variable => ["name", "scope"]
  .map(property => variable.get(property))
  .every(Boolean);

export const hasInvalidVariables = (variables, responseId) => {
  const variablesToCheck = variables.map((value, key) => {
    if (key === LOCAL_VARIABLE_SCOPE && value.has(responseId)) {
      return value.get(responseId);
    }

    return value;
  }).valueSeq().flatten();

  return variablesToCheck.some(variable => !variable.get("isValid"));
};

/**
 * @param {object} variable
 * @param {string} responseId
 * @returns {object}
 */
export function constructResponseReferences(variable, responseId) {
  try {
    return variable.responseReferenceCounts.reduce(
      (reduction, counts, respId) => {
        let count = counts.saved;

        if (respId === responseId) {
          count += counts.unsaved;
        }

        return reduction.concat(Array(count).fill(respId));
      },
      Immutable.List()
    );
  } catch (e) {
    trackException(e);

    return variable.responseReferences;
  }
}

/**
 * @param {unknown} stateVariable
 * @param {unknown} action
 * @returns {unknown}
 */
export function updateFromServer(stateVariable, action) {
  /*
    This is called on successful create,update, or delete.
    Updates the id and counts
  */
  let variable = stateVariable;
  const data = action.response && action.response.data && action.response.data.variable;
  const { responseId } = action;
  const savedCounts = data
    ? Immutable.List(data.response_references).countBy(v => v)
    : null;

  variable = variable
    .merge({
      id: data ? data._id : variable.id,
      modified: false
    })
    .mergeIn(["responseReferenceCounts", responseId], {
      saved: savedCounts ? savedCounts.get(responseId) : 0,
      unsaved: 0
    });

  return variable;
}

export const createVariableRecord = (variable, saved) => {
  /* variable: variable object from server (saved = true) OR created by user */
  const keyConvertedVariable = keyConverter(variable);
  // isActive: Active on the backend
  const active = !saved || keyConvertedVariable.active;
  let responseReferences = Immutable.List(keyConvertedVariable.responseReferences || []);

  if (responseReferences.isEmpty() && active) {
    responseReferences = Immutable.List(
      [(keyConvertedVariable.responseId || keyConvertedVariable.originId)]
    );
  }

  let newVariableRecord = new VariableRecord(keyConvertedVariable).merge({
    responseReferences,
    modified: !saved
  });

  if (saved) {
    const savedCounts = responseReferences.countBy(v => v);
    newVariableRecord = newVariableRecord.merge({
      responseReferenceCounts: savedCounts.map(
        savedCount => new ReferenceCounts({ saved: savedCount || 1 })
      )
    });
  } else if (newVariableRecord.responseId) {
    /* this requires globals to have response_id set,
      which seems odd since we remove it later
    */
    newVariableRecord = addResponseId(
      newVariableRecord,
      newVariableRecord.responseId
    );
  }

  if (newVariableRecord.scope === GLOBAL_VARIABLE_SCOPE) {
    newVariableRecord = newVariableRecord.set("responseId", null);
  }

  if (newVariableRecord.get("id") === null) {
    newVariableRecord = newVariableRecord.merge({ id: mongoObjectId() });
  }

  return newVariableRecord;
};

export const mapValidationToType = {
  string: "string",
  phone_number: "string",
  email: "string",
  pnr: "string",
  number: "long",
  boolean: "bool"
};

export const mapTypeToValidation = {
  string: "string",
  long: "number",
  bool: "boolean"
};

/**
 * if it's a capture or request block, get the id's of the global variables
 * go through all the global variables in the state and add the response id
 * to the references list
 *
 * @param {List} messages
 * @param {Array} variableIds
 * @returns {Array}
 */
export const getAllVariableIdsFromMessages = (messages, variableIds = []) => {
  messages.forEach((message) => {
    if (message.type === "capture") {
      variableIds.push(message.variableId);
    }

    if (message.type === "http_request_recipe" || message.type === "variable_override") {
      message.variablesData.forEach((variable) => {
        variableIds.push(variable.variableId);
      });
    }

    if (message.type === "list_selection_template") {
      variableIds.push(message.variableId);
    }

    if (message.type === "scheduled_block") {
      variableIds.concat(getAllVariableIdsFromMessages(message.affirmativeMessages, variableIds));
      variableIds.concat(getAllVariableIdsFromMessages(message.negativeMessages, variableIds));
    }

    if (message.type === "conditionals_block") {
      message.statements.forEach((statement) => {
        variableIds.concat(getAllVariableIdsFromMessages(statement.messages, variableIds));
      });
    }
  });

  return variableIds;
};
